import React, { useState, useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';
import MiniGameWrapper from './MiniGameWrapper';
import { MiniGameResult, PersonalityType } from '@/types/career-sim';
import { ThumbsUp, ThumbsDown } from 'lucide-react';

interface NetworkCard {
  id: string;
  name: string;
  title: string;
  offer: string;
  impact: {
    reputation?: number;
    funds?: number;
    fanbase?: number;
  };
}

const cards: NetworkCard[] = [
  { id: '1', name: 'Label Scout', title: 'Major Label Rep', offer: 'Sign with us! $5k advance but lose creative control', impact: { funds: 5000, reputation: -10 } },
  { id: '2', name: 'Producer Mike', title: 'Indie Producer', offer: 'Let\'s collab! $500 studio fee, great sound', impact: { reputation: 15, funds: -500 } },
  { id: '3', name: 'DJ Luna', title: 'Radio Host', offer: 'Play your track on my show for exposure', impact: { fanbase: 50, reputation: 10 } },
  { id: '4', name: 'Influencer Jay', title: 'TikTok Star (2M)', offer: 'Feature you in my video for $300', impact: { fanbase: 200, funds: -300 } },
  { id: '5', name: 'Venue Owner', title: 'Club Manager', offer: 'Open for our headliner, no pay but great exposure', impact: { fanbase: 30, reputation: 5 } },
  { id: '6', name: 'Music Blogger', title: 'Blog Editor', offer: 'Review your album, guaranteed positive coverage for $200', impact: { reputation: 8, funds: -200 } },
  { id: '7', name: 'Fellow Artist', title: 'Underground Rapper', offer: 'Split EP collab? 50/50 costs and profits', impact: { reputation: 12, fanbase: 25 } },
  { id: '8', name: 'Sponsor Rep', title: 'Brand Manager', offer: 'Promote our energy drink for $1k', impact: { funds: 1000, reputation: -5 } },
];

interface NetworkingGameProps {
  onComplete: (result: MiniGameResult) => void;
  onExit: () => void;
  personality: PersonalityType;
}

// Component handles networking mini-game with personality-based bonuses
const NetworkingGame: React.FC<NetworkingGameProps> = ({ onComplete, onExit, personality }) => {
  const [currentCards, setCurrentCards] = useState<NetworkCard[]>([]);
  const [cardIndex, setCardIndex] = useState(0);
  const [decisions, setDecisions] = useState<{ accepted: number; declined: number }>({ accepted: 0, declined: 0 });
  const [timeLeft, setTimeLeft] = useState(20);
  const [totalImpact, setTotalImpact] = useState({ reputation: 0, funds: 0, fanbase: 0 });
  const controls = useAnimation();

  useEffect(() => {
    const shuffled = [...cards].sort(() => Math.random() - 0.5).slice(0, 8);
    setCurrentCards(shuffled);
  }, []);

  useEffect(() => {
    if (timeLeft <= 0 || cardIndex >= currentCards.length) {
      endGame();
      return;
    }

    const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
    return () => clearTimeout(timer);
  }, [timeLeft, cardIndex]);

  const handleSwipe = async (direction: 'left' | 'right') => {
    if (cardIndex >= currentCards.length) return;

    const card = currentCards[cardIndex];
    
    // Animate card
    await controls.start({
      x: direction === 'left' ? -500 : 500,
      opacity: 0,
      transition: { duration: 0.3 }
    });

    if (direction === 'right') {
      // Accepted
      setDecisions(prev => ({ ...prev, accepted: prev.accepted + 1 }));
      setTotalImpact(prev => ({
        reputation: prev.reputation + (card.impact.reputation || 0),
        funds: prev.funds + (card.impact.funds || 0),
        fanbase: prev.fanbase + (card.impact.fanbase || 0),
      }));
    } else {
      // Declined
      setDecisions(prev => ({ ...prev, declined: prev.declined + 1 }));
    }

    setCardIndex(prev => prev + 1);
    controls.set({ x: 0, opacity: 1 });
  };

  const endGame = () => {
    const totalDecisions = decisions.accepted + decisions.declined;
    const score = totalDecisions > 0 ? (decisions.accepted / totalDecisions) * 100 : 50;
    
    // Apply personality bonuses
    let multiplier = 1.0;
    if (personality === 'industry' && decisions.accepted > decisions.declined) {
      multiplier = 1.3; // Industry Player benefits from networking
    } else if (personality === 'purist' && decisions.declined > decisions.accepted) {
      multiplier = 1.2; // Purist bonus for maintaining independence
    }
    
    setTimeout(() => {
      onComplete({
        score,
        multiplier,
        success: true,
      });
    }, 500);
  };

  const currentCard = currentCards[cardIndex];

  return (
    <MiniGameWrapper
      title="🤝 Networking Event"
      instructions="Swipe right to accept, left to decline!"
      timeLeft={timeLeft}
      maxTime={20}
      onExit={onExit}
    >
      <div className="flex flex-col h-full items-center justify-center">
        {/* Stats */}
        <div className="flex gap-4 mb-6 text-sm text-white">
          <span>Accepted: {decisions.accepted}</span>
          <span>Declined: {decisions.declined}</span>
          <span>Remaining: {currentCards.length - cardIndex}</span>
        </div>

        {/* Card */}
        {currentCard && (
          <motion.div
            animate={controls}
            className="w-full max-w-md h-80 bg-gradient-to-br from-purple-800 to-blue-800 rounded-2xl p-6 shadow-2xl border-2 border-purple-400"
          >
            <div className="flex flex-col h-full justify-between">
              <div>
                <div className="text-4xl mb-4 text-center">👤</div>
                <h3 className="text-2xl font-bold text-white mb-1">{currentCard.name}</h3>
                <p className="text-purple-200 text-sm mb-4">{currentCard.title}</p>
                <p className="text-white text-lg italic mb-4">"{currentCard.offer}"</p>
              </div>

              <div className="bg-black/30 rounded-lg p-3 space-y-1 text-sm">
                <div className="text-gray-300 font-semibold mb-2">Impact:</div>
                {currentCard.impact.reputation && (
                  <div className={currentCard.impact.reputation > 0 ? 'text-green-400' : 'text-red-400'}>
                    ⭐ {currentCard.impact.reputation > 0 ? '+' : ''}{currentCard.impact.reputation} Reputation
                  </div>
                )}
                {currentCard.impact.funds && (
                  <div className={currentCard.impact.funds > 0 ? 'text-green-400' : 'text-red-400'}>
                    💰 {currentCard.impact.funds > 0 ? '+' : ''}{currentCard.impact.funds} Funds
                  </div>
                )}
                {currentCard.impact.fanbase && (
                  <div className="text-pink-400">
                    👥 +{currentCard.impact.fanbase} Fans
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-4 mt-6">
          <motion.button
            onClick={() => handleSwipe('left')}
            className="px-8 py-4 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl flex items-center gap-2 shadow-lg"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            disabled={!currentCard}
          >
            <ThumbsDown className="h-5 w-5" />
            Decline
          </motion.button>
          <motion.button
            onClick={() => handleSwipe('right')}
            className="px-8 py-4 bg-green-600 hover:bg-green-700 text-white font-bold rounded-xl flex items-center gap-2 shadow-lg"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            disabled={!currentCard}
          >
            <ThumbsUp className="h-5 w-5" />
            Accept
          </motion.button>
        </div>

        <div className="mt-4 text-xs text-gray-400">
          Keyboard: ← to decline, → to accept
        </div>
      </div>
    </MiniGameWrapper>
  );
};

export default NetworkingGame;
